import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { findMatches, normalise, pageOf, prepare, MIN_QUERY_LENGTH } from '@theme/collection-text-match';
import {
  INDEX_PAGE_SIZE,
  MAX_INDEX_PAGES,
  fill,
  indexKey,
  indexPlan,
  joinBatches,
  matchOrder,
  readState,
  runLimited,
  validBatch,
  writeState,
} from '@theme/collection-text-state';

/**
 * Collection text filter ("Search this collection"), owner-approved option C (2026-10-16).
 *
 * Products: the current collection's products after its active filters, in its current sort, from the data section
 * sections/lymi-text-index.liquid requested on the collection URL (Section Rendering API). It is loaded the first
 * time text is entered, then once per filter/sort combination, and kept in memory for this page only. Up to
 * MAX_INDEX_PAGES requests of INDEX_PAGE_SIZE products; a larger filtered collection is never searched in part (the
 * box says so instead). Matching: assets/collection-text-match.js.
 *
 * Cards: the products of the page shown, at most the section's page size, as Shopify renders them. Cards already on
 * the page are reused; others come from the full-card view (/products/<handle>?view=wishlist-card), at most
 * CARD_REQUESTS at a time. A card that fails to load keeps its place, with Try again; it is never dropped.
 *
 * Every update has a number; a response for an older update is ignored, and its card requests are aborted.
 * If anything here fails, the collection keeps Shopify's own results and paging.
 */

const CARD_REQUESTS = 4;
const CARD_CACHE_LIMIT = 60;
const TYPING_DELAY = 250;

/** @param {string} html */
function fragmentOf(html) {
  return document.createRange().createContextualFragment(html);
}

class TextFilterController {
  /** @type {Map<string, Promise<{ items: ReturnType<typeof prepare>[], total: number, plan: 'complete' | 'too-large' }>>} */
  #indexes = new Map();
  /** @type {Map<number, Element>} product id -> its card (product-card element) */
  #cards = new Map();
  #generation = 0;
  /** @type {AbortController | null} */
  #cardAbort = null;
  /** @type {number | undefined} */
  #typingTimer;
  #active = false;
  /** Text as typed; the URL keeps it too. */
  text = '';

  get root() {
    return /** @type {HTMLElement | null} */ (document.querySelector('collection-text-filter'));
  }

  get boxes() {
    return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('collection-text-filter')));
  }

  get grid() {
    return /** @type {HTMLElement | null} */ (document.querySelector('collection-component ul.product-grid'));
  }

  get sectionId() {
    const form = document.querySelector('facets-form-component[data-page-type="collection"]');
    return form?.getAttribute('section-id') ?? '';
  }

  /** @param {string} key */
  t(key) {
    return this.root?.dataset[key] ?? '';
  }

  /** One shared polite live region, so the two boxes never announce twice. */
  announce(message) {
    let region = document.getElementById('LymiTextFilterStatus');
    if (!region) {
      region = document.createElement('div');
      region.id = 'LymiTextFilterStatus';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    region.textContent = '';
    window.setTimeout(() => {
      if (region) region.textContent = message;
    }, 60);
  }

  start() {
    window.__lymiTextFilter = true;
    document.documentElement.classList.remove('lymi-text-pending');
    if (!this.root || !this.grid) return;

    document.addEventListener('input', this.#onInput, true);
    document.addEventListener('change', this.#onChange, true);
    document.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown, true);
    document.addEventListener(StandardEvents.collectionUpdate, this.#onCollectionUpdate);
    document.addEventListener('lymi:collection-rendered', this.#onRendered);
    document.addEventListener('lymi:collection-will-render', this.#onWillRender);

    this.#harvestCards();
    const state = readState(new URLSearchParams(location.search));
    this.text = state.text;
    this.#syncInputs();
    if (this.#isSearchable(this.text)) this.apply({ fromUrl: true });
  }

  /** @param {string} text */
  #isSearchable(text) {
    return normalise(text).replace(/ /g, '').length >= MIN_QUERY_LENGTH;
  }

  /** Keeps the cards Shopify rendered on this page, so they are shown rather than requested again. */
  #harvestCards() {
    const grid = this.grid;
    if (!grid || this.#active) return;
    for (const card of grid.querySelectorAll(':scope > li > product-card[data-product-id]')) {
      this.#remember(Number(card.getAttribute('data-product-id')), card);
    }
  }

  /** @param {number} id @param {Element} card */
  #remember(id, card) {
    this.#cards.delete(id);
    this.#cards.set(id, card);
    while (this.#cards.size > CARD_CACHE_LIMIT) this.#cards.delete(this.#cards.keys().next().value);
  }

  #syncInputs() {
    for (const box of this.boxes) {
      const input = box.querySelector('input[type="search"]');
      if (input instanceof HTMLInputElement && input.value !== this.text && document.activeElement !== input) {
        input.value = this.text;
      }
      box.toggleAttribute('data-has-text', this.text !== '');
    }
    for (const chip of document.querySelectorAll('[data-text-filter-chip]')) {
      if (!(chip instanceof HTMLElement)) continue;
      const show = this.#isSearchable(this.text);
      chip.hidden = !show;
      const label = chip.querySelector('[data-text-filter-chip-label]');
      if (label) label.textContent = fill(this.t('textChip'), { text: this.text.trim() });
      chip.setAttribute('aria-label', fill(this.t('textChipRemove'), { text: this.text.trim() }));
    }
  }

  /** @param {string} message @param {'info' | 'error' | ''} [kind] @param {boolean} [retry] */
  #status(message, kind = '', retry = false) {
    for (const box of this.boxes) {
      const line = box.querySelector('[data-text-filter-status]');
      if (line) line.textContent = message;
      box.dataset.state = kind;
      const button = box.querySelector('[data-text-filter-retry]');
      if (button instanceof HTMLElement) button.hidden = !retry;
    }
  }

  // ---------- Events ----------

  #onInput = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('collection-text-filter')) return;
    event.stopPropagation();
    this.text = input.value.slice(0, 100);
    for (const box of this.boxes) {
      const other = box.querySelector('input[type="search"]');
      if (other instanceof HTMLInputElement && other !== input) other.value = this.text;
      box.toggleAttribute('data-has-text', this.text !== '');
    }
    window.clearTimeout(this.#typingTimer);
    this.#typingTimer = window.setTimeout(() => this.apply({ typed: true }), TYPING_DELAY);
  };

  #onChange = (/** @type {Event} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.closest('collection-text-filter')) {
      event.stopPropagation();
      return;
    }
    // A normal sort choice always wins over Best match: uncheck it before the filters build the new address.
    if (input.name === 'sort_by') {
      for (const best of document.querySelectorAll('input[name="lymi_text_sort"]')) {
        if (best instanceof HTMLInputElement) best.checked = false;
      }
      return;
    }
    if (input.name === 'lymi_text_sort') {
      event.stopPropagation();
      this.#chooseBestMatch(input);
    }
  };

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-text-filter-clear], [data-text-filter-chip]')) {
      event.preventDefault();
      const fromChip = Boolean(target.closest('[data-text-filter-chip]'));
      this.clear({ focus: fromChip ? 'toggle' : 'input', from: target });
    } else if (target.closest('[data-text-filter-retry]')) {
      this.#indexes.delete(this.#currentKey());
      this.apply({});
    } else if (target.closest('[data-text-filter-page]')) {
      const button = /** @type {HTMLElement} */ (target.closest('[data-text-filter-page]'));
      this.#goToPage(Number(button.dataset.textFilterPage));
    } else if (target.closest('[data-text-filter-card-retry]')) {
      const li = target.closest('li[data-text-filter-item]');
      if (li instanceof HTMLElement) this.#loadCardInto(li, this.#generation);
    }
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('collection-text-filter')) return;
    // Inside the mobile drawer the box sits in the filters form: Enter must not submit (and reload) the page.
    if (event.key === 'Enter') {
      event.preventDefault();
      window.clearTimeout(this.#typingTimer);
      // The box's own value, in case it changed without an input event (autofill, for example)
      this.text = input.value.slice(0, 100);
      this.apply({ typed: true });
    } else if (event.key === 'Escape' && input.value) {
      event.preventDefault();
      event.stopPropagation();
      this.clear({ focus: 'input', from: input });
    }
  };

  /** The filters or sort are about to change: the current results no longer apply. */
  #onCollectionUpdate = () => {
    if (!this.#isSearchable(readState(new URLSearchParams(location.search)).text)) return;
    this.#generation++;
    this.#cardAbort?.abort();
    this.#setLoading(true);
  };

  /**
   * Horizon is about to re-render the collection. Its in-place update would otherwise rewrite the cards shown here
   * (which are kept for reuse) into other products' cards, so the text results leave the grid first.
   */
  #onWillRender = () => {
    this.#generation++;
    this.#cardAbort?.abort();
    this.#detachResults();
  };

  #detachResults() {
    const grid = this.grid;
    if (grid?.querySelector(':scope > li[data-text-filter-item]')) grid.replaceChildren();
    this.#removePagination();
  }

  /** Horizon has re-rendered the collection (filters, sort, Clear all, Back/Forward). */
  #onRendered = () => {
    this.#active = false;
    this.#harvestCards();
    const state = readState(new URLSearchParams(location.search));
    this.text = state.text;
    this.#syncInputs();
    if (this.#isSearchable(this.text)) this.apply({ fromUrl: true });
    else this.#deactivate();
  };

  // ---------- State and address ----------

  #currentKey() {
    return indexKey(location.pathname, new URLSearchParams(location.search));
  }

  /** @param {{ page?: number, best?: boolean, push?: boolean }} change */
  #writeUrl({ page = 1, best, push = false }) {
    const params = new URLSearchParams(location.search);
    const current = readState(params);
    const next = writeState(params, { text: this.text.trim(), page, best: best ?? current.best });
    const url = `${location.pathname}${next.toString() ? `?${next}` : ''}${location.hash}`;
    if (url === `${location.pathname}${location.search}${location.hash}`) return;
    if (push) history.pushState({ lymiText: true }, '', url);
    else history.replaceState(history.state, '', url);
  }

  /** @param {HTMLInputElement} input */
  #chooseBestMatch(input) {
    for (const radio of document.querySelectorAll('input[name="sort_by"], input[name="lymi_text_sort"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = radio === input;
    }
    input.closest('details')?.removeAttribute('open');
    // Best match orders the collection's default order by match quality, so the explicit sort is dropped.
    const params = new URLSearchParams(location.search);
    params.delete('sort_by');
    const next = writeState(params, { text: this.text.trim(), page: 1, best: true });
    history.pushState({ lymiText: true }, '', `${location.pathname}?${next}${location.hash}`);
    this.apply({});
  }

  // ---------- Applying ----------

  /**
   * Shows the matches for the current text, filters and sort.
   * @param {{ typed?: boolean, fromUrl?: boolean, page?: number }} options
   */
  async apply({ typed = false, fromUrl = false, page } = {}) {
    const generation = ++this.#generation;
    this.#cardAbort?.abort();
    this.#syncInputs();

    if (!this.#isSearchable(this.text)) {
      // Too short to search (or empty): Shopify's own results, and no text in the address
      const showing =
        this.#active ||
        document.documentElement.classList.contains('lymi-text-loading') ||
        new URLSearchParams(location.search).has('text');
      if (showing) this.clear({ keepText: true });
      this.#status(this.text.trim() ? this.t('textMin') : '');
      return;
    }

    const params = new URLSearchParams(location.search);
    const state = readState(params);
    const wasActive = this.#active;
    if (typed) this.#writeUrl({ page: 1, push: !wasActive && !state.text });
    const targetPage = page ?? (typed ? 1 : readState(new URLSearchParams(location.search)).page);

    this.#setLoading(true);
    this.#status(fill(this.t('textLoading'), { text: this.text.trim() }), 'info');

    let index;
    try {
      index = await this.#index();
    } catch {
      if (generation !== this.#generation) return;
      this.#showIndexFailure();
      return;
    }
    if (generation !== this.#generation) return;

    if (index.plan === 'too-large') {
      this.#showTooLarge();
      return;
    }

    const order = matchOrder(readState(new URLSearchParams(location.search)));
    const matches = findMatches(index.items, this.text, order);
    const shown = pageOf(matches, targetPage, this.#pageSize());
    if (shown.page !== targetPage) this.#writeUrl({ page: shown.page });

    this.#active = true;
    this.#setBestMatch(order === 'best');
    this.#renderResults(matches, shown, index.total, generation, { fromUrl });
  }

  #pageSize() {
    return Math.max(1, Number(this.root?.dataset.pageSize) || 6);
  }

  /** @returns {Promise<{ items: ReturnType<typeof prepare>[], total: number, plan: 'complete' | 'too-large' }>} */
  #index() {
    const key = this.#currentKey();
    let promise = this.#indexes.get(key);
    if (!promise) {
      promise = this.#loadIndex();
      this.#indexes.set(key, promise);
      promise.catch(() => this.#indexes.delete(key));
    }
    return promise;
  }

  async #loadIndex() {
    const collection = this.root?.dataset.collection ?? '';
    const base = new URLSearchParams(location.search);
    for (const name of ['text', 'text_page', 'text_sort', 'page']) base.delete(name);

    const batch = async (/** @type {number} */ page) => {
      const params = new URLSearchParams(base);
      params.set('section_id', 'lymi-text-index');
      if (page > 1) params.set('page', String(page));
      const response = await fetch(`${location.pathname}?${params}`, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`index ${response.status}`);
      const script = fragmentOf(await response.text()).querySelector('script[data-lymi-text-index]');
      const data = JSON.parse(script?.textContent ?? 'null');
      if (!validBatch(data, { collection, page })) throw new Error('index invalid');
      return data;
    };

    const first = await batch(1);
    if (indexPlan(first.pages) === 'too-large') return { items: [], total: first.total, plan: 'too-large' };
    const rest = [];
    for (let page = 2; page <= first.pages; page++) rest.push(await batch(page));
    const joined = joinBatches([first, ...rest]);
    if (!joined.complete) throw new Error('index incomplete');
    // Descriptions arrive as text with entities; decode them as plain text in an inert document, never into the page.
    const decoder = new DOMParser();
    const items = joined.items.map((item) =>
      prepare({ ...item, text: decoder.parseFromString(`<!doctype html><body>${item.text ?? ''}`, 'text/html').body.textContent ?? '' })
    );
    return { items, total: first.total, plan: 'complete' };
  }

  // ---------- Rendering ----------

  /** @param {boolean} loading */
  #setLoading(loading) {
    const html = document.documentElement;
    html.classList.toggle('lymi-text-loading', loading);
    if (loading) html.classList.remove('lymi-text-error');
    this.grid?.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) this.#resultsNote(fill(this.t('textLoading'), { text: this.text.trim() }));
  }

  /** The line above the grid: loading, failure or scope. @param {string} message @param {boolean} [retry] */
  #resultsNote(message, retry = false) {
    const grid = this.grid;
    if (!grid) return;
    let note = /** @type {HTMLElement | null} */ (document.getElementById('LymiTextResultsNote'));
    if (!note) {
      note = document.createElement('div');
      note.id = 'LymiTextResultsNote';
      note.className = 'lymi-text-note';
      note.tabIndex = -1;
      note.innerHTML = '<p class="lymi-text-note__text"></p><button type="button" class="lymi-text-note__button" data-text-filter-retry hidden></button>';
      grid.before(note);
    }
    const text = note.querySelector('p');
    if (text) text.textContent = message;
    const button = note.querySelector('button');
    if (button instanceof HTMLElement) {
      button.textContent = this.t('textRetry');
      button.hidden = !retry;
    }
    note.hidden = !message;
  }

  #showIndexFailure() {
    this.#active = false;
    document.documentElement.classList.remove('lymi-text-loading', 'lymi-text-active');
    document.documentElement.classList.add('lymi-text-error');
    this.grid?.setAttribute('aria-busy', 'false');
    this.#removePagination();
    this.#status(this.t('textFailed'), 'error', true);
    this.#resultsNote(this.t('textFailed'), true);
    this.announce(this.t('textFailed'));
    // The grid may hold earlier text results: put Shopify's own results back.
    if (this.grid?.querySelector('li[data-text-filter-item]')) this.#restoreShopify();
  }

  #showTooLarge() {
    this.#active = false;
    document.documentElement.classList.remove('lymi-text-loading', 'lymi-text-active');
    this.grid?.setAttribute('aria-busy', 'false');
    this.#removePagination();
    this.#status(this.t('textTooLarge'), 'error');
    this.#resultsNote(this.t('textTooLarge'));
    this.announce(this.t('textTooLarge'));
    if (this.grid?.querySelector('li[data-text-filter-item]')) this.#restoreShopify();
  }

  /** @param {boolean} best */
  #setBestMatch(best) {
    for (const radio of document.querySelectorAll('input[name="lymi_text_sort"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = best;
    }
    if (best) {
      for (const radio of document.querySelectorAll('input[name="sort_by"]')) {
        if (radio instanceof HTMLInputElement) radio.checked = false;
      }
    }
  }

  /**
   * @param {Array<ReturnType<typeof prepare> & { tier: number }>} matches
   * @param {{ items: Array<ReturnType<typeof prepare>>, page: number, pages: number }} shown
   * @param {number} total
   * @param {number} generation
   * @param {{ fromUrl?: boolean }} options
   */
  #renderResults(matches, shown, total, generation, { fromUrl = false } = {}) {
    const grid = this.grid;
    if (!grid) return;
    const html = document.documentElement;
    html.classList.add('lymi-text-active');
    html.classList.remove('lymi-text-loading', 'lymi-text-error');
    grid.setAttribute('aria-busy', 'false');

    const text = this.text.trim();
    const countText = fill(matches.length === 1 ? this.t('textCountOne') : this.t('textCountOther'), {
      count: matches.length,
      total,
      text,
    });
    for (const count of document.querySelectorAll('collection-component .products-count-wrapper [role="status"]')) {
      count.textContent = countText;
    }
    for (const button of document.querySelectorAll('.facets__see-results')) {
      button.textContent = fill(matches.length === 1 ? this.t('textSeeOne') : this.t('textSeeOther'), { count: matches.length });
    }
    this.#status(this.t('textScope'), 'info');
    this.#resultsNote('');
    this.announce(countText);

    // Cards: keep Shopify's cards for this page's products, request the others
    this.#cardAbort = new AbortController();
    const items = shown.items.map((item) => this.#itemFor(item));
    grid.replaceChildren(...items);

    if (matches.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'lymi-text-empty';
      empty.dataset.textFilterItem = 'empty';
      const message = document.createElement('p');
      message.textContent = fill(this.t('textNone'), { text });
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'lymi-text-empty__clear';
      clear.dataset.textFilterClear = '';
      clear.textContent = this.t('textClear');
      empty.append(message, clear);
      grid.replaceChildren(empty);
    }

    const pending = items.filter((li) => li.dataset.state === 'loading');
    runLimited(
      pending.map((li) => () => this.#loadCardInto(li, generation)),
      CARD_REQUESTS
    );

    this.#renderPagination(shown);
    if (!fromUrl) this.#syncInputs();
  }

  /** @param {ReturnType<typeof prepare>} item */
  #itemFor(item) {
    const li = document.createElement('li');
    li.className = 'product-grid__item lymi-text-item';
    li.dataset.textFilterItem = String(item.id);
    li.dataset.handle = item.handle;
    li.dataset.title = item.name ?? '';
    const card = this.#cards.get(item.id);
    if (card) {
      li.append(card);
      li.dataset.state = 'ready';
    } else {
      li.dataset.state = 'loading';
      const placeholder = document.createElement('p');
      placeholder.className = 'lymi-text-item__message';
      placeholder.textContent = this.t('textCardLoading');
      li.append(placeholder);
    }
    return li;
  }

  /** @param {HTMLElement} li @param {number} generation */
  async #loadCardInto(li, generation) {
    const id = Number(li.dataset.textFilterItem);
    const handle = li.dataset.handle ?? '';
    li.dataset.state = 'loading';
    const signal = this.#cardAbort?.signal;
    try {
      const root = this.root?.dataset.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=wishlist-card`, {
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) throw new Error(`card ${response.status}`);
      const html = await response.text();
      if (generation !== this.#generation || !li.isConnected) return;
      const card = fragmentOf(html).querySelector('[data-wishlist-card] product-card[data-product-id]');
      if (!card || Number(card.getAttribute('data-product-id')) !== id) throw new Error('card mismatch');
      this.#remember(id, card);
      li.replaceChildren(card);
      li.dataset.state = 'ready';
    } catch (error) {
      if (generation !== this.#generation || !li.isConnected || /** @type {Error} */ (error)?.name === 'AbortError') return;
      this.#cardFailed(li);
    }
  }

  /** A card that could not load keeps its place: its name, a link to it and Try again. @param {HTMLElement} li */
  #cardFailed(li) {
    li.dataset.state = 'error';
    const box = document.createElement('div');
    box.className = 'lymi-text-item__error';
    const message = document.createElement('p');
    message.textContent = this.t('textCardFailed');
    const link = document.createElement('a');
    link.href = `${this.root?.dataset.root || '/'}products/${encodeURIComponent(li.dataset.handle ?? '')}`;
    link.textContent = li.dataset.title || li.dataset.handle || '';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.dataset.textFilterCardRetry = '';
    retry.textContent = this.t('textRetry');
    box.append(link, message, retry);
    li.replaceChildren(box);
  }

  #removePagination() {
    document.getElementById('LymiTextPagination')?.remove();
  }

  /** @param {{ page: number, pages: number }} shown */
  #renderPagination({ page, pages }) {
    this.#removePagination();
    const grid = this.grid;
    if (!grid || pages <= 1) return;
    const nav = document.createElement('nav');
    nav.id = 'LymiTextPagination';
    nav.className = 'lymi-text-pagination';
    nav.setAttribute('aria-label', this.t('textPagination'));
    const list = document.createElement('ul');
    list.className = 'lymi-text-pagination__list';
    const add = (/** @type {string} */ label, /** @type {number} */ target, /** @type {{ current?: boolean, aria?: string, disabled?: boolean }} */ opts = {}) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lymi-text-pagination__button';
      button.textContent = label;
      button.dataset.textFilterPage = String(target);
      if (opts.aria) button.setAttribute('aria-label', opts.aria);
      if (opts.current) button.setAttribute('aria-current', 'page');
      if (opts.disabled) button.disabled = true;
      li.append(button);
      list.append(li);
    };
    add('‹', page - 1, { aria: this.t('textPrevious'), disabled: page <= 1 });
    for (let n = 1; n <= pages; n++) add(String(n), n, { current: n === page, aria: fill(this.t('textPageLabel'), { page: n, pages }) });
    add('›', page + 1, { aria: this.t('textNext'), disabled: page >= pages });
    nav.append(list);
    grid.after(nav);
  }

  /** @param {number} page */
  async #goToPage(page) {
    if (!Number.isFinite(page) || page < 1) return;
    this.#writeUrl({ page, push: true });
    await this.apply({ page });
    const count = document.querySelector('collection-component .products-count-wrapper');
    const target = /** @type {HTMLElement | null} */ (count ?? this.grid);
    if (target) {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  // ---------- Clearing ----------

  /**
   * Removes the text: Shopify's own results, counts and paging come back.
   * @param {{ focus?: 'input' | 'toggle', from?: Element | null, keepText?: boolean }} options
   */
  clear({ focus, from = null, keepText = false } = {}) {
    window.clearTimeout(this.#typingTimer);
    this.#generation++;
    this.#cardAbort?.abort();
    if (!keepText) this.text = '';
    const params = new URLSearchParams(location.search);
    const hadText = params.has('text');
    const best = readState(params).best;
    const next = writeState(params, { text: '' });
    if (hadText) history.pushState({ lymiText: true }, '', `${location.pathname}${next.toString() ? `?${next}` : ''}${location.hash}`);
    this.#syncInputs();
    this.#status('');
    const wasShowing = this.#active || document.documentElement.classList.contains('lymi-text-loading') || best;
    this.#deactivate();
    if (wasShowing) this.#restoreShopify();

    if (focus === 'input') {
      const box = from?.closest('collection-text-filter') ?? this.root;
      const input = box?.querySelector('input[type="search"]');
      if (input instanceof HTMLElement) input.focus();
    } else if (focus === 'toggle') {
      const toggle = document.querySelector('.facets-toggle__button');
      if (toggle instanceof HTMLElement) toggle.focus();
    }
  }

  #deactivate() {
    this.#active = false;
    document.documentElement.classList.remove('lymi-text-active', 'lymi-text-loading', 'lymi-text-error');
    this.grid?.setAttribute('aria-busy', 'false');
    this.#removePagination();
    this.#resultsNote('');
    for (const radio of document.querySelectorAll('input[name="lymi_text_sort"]')) {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    }
  }

  /** Shopify's own results for the address now shown, through Horizon's renderer. */
  #restoreShopify() {
    const id = this.sectionId;
    if (!id) return;
    this.#detachResults();
    sectionRenderer
      .renderSection(id)
      .then(() => document.dispatchEvent(new CustomEvent('lymi:collection-rendered')))
      .catch(() => location.reload());
  }
}

const controller = new TextFilterController();

if (!customElements.get('collection-text-filter')) {
  customElements.define('collection-text-filter', class extends HTMLElement {});
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => controller.start(), { once: true });
else controller.start();
